import React, { useMemo } from 'react';
import { Plus, Send, Users, Heart, Flame, Star, Inbox, ChevronRight, CalendarClock } from 'lucide-react';
import { TaskRow } from '../components/TaskRow.jsx';
import { todayStr, getNextDueDate, inferRecurringStart, snapDeadlineToPattern } from '../lib/dates.js';

export function DashboardPage({ theme, profile, partner, pair, pairStats, tasks, completions, onAddTask, onComplete, onDelete, onEdit, onGoToShopping, onGoToTasks, onGoToStats, onScrollToCompletedToday }) {
  const today = todayStr();
  const partnerFirstName = partner?.name?.split(' ')[0];

  const myActionable = useMemo(() => {
    return tasks.filter(t => {
      if (t.list === 'sent' && t.createdBy === profile?.id && t.assignedTo !== profile?.id) return false;
      return true;
    });
  }, [tasks, profile]);

  // Returns the next occurrence date for a task as YYYY-MM-DD
  const nextOccurrenceDate = (t) => {
    let currentDate;
    if (t.deadline) {
      currentDate = t.deadline.split('T')[0];
      // Snap weekday tasks forward to first valid day (handles edited tasks)
      if (t.repeatType === 'weekday') {
        currentDate = snapDeadlineToPattern(t, currentDate);
      }
      if (!t.repeatType || t.repeatType === 'none') return currentDate;
    } else if (t.repeatType && t.repeatType !== 'none') {
      currentDate = inferRecurringStart(t);
      if (!currentDate) return '9999-12-31';
    } else {
      return '9999-12-31';
    }

    // Walk forward to find next occurrence at or after today
    let safety = 0;
    while (currentDate < today && safety < 800) {
      const next = getNextDueDate(currentDate, t.repeatType, t.interval, t.monthDay, t.weekday, t.weekdays || t.repeatWeekdays);
      if (!next || next <= currentDate) break;
      currentDate = next;
      safety++;
    }
    return currentDate;
  };

  // Does this task have an occurrence on today or in the past (i.e. "needs attention now")?
  const occursTodayOrEarlier = (t) => {
    if (!t.deadline && (!t.repeatType || t.repeatType === 'none')) return true;

    let currentDate;
    if (t.deadline) {
      currentDate = t.deadline.split('T')[0];
      if (t.repeatType === 'weekday') {
        currentDate = snapDeadlineToPattern(t, currentDate);
      }
      if (!t.repeatType || t.repeatType === 'none') {
        return currentDate <= today;
      }
    } else if (t.repeatType && t.repeatType !== 'none') {
      currentDate = inferRecurringStart(t);
      if (!currentDate) return false;
    } else {
      return false;
    }

    // Walk through occurrences
    let safety = 0;
    while (currentDate <= today && safety < 800) {
      if (currentDate <= today) return true;
      const next = getNextDueDate(currentDate, t.repeatType, t.interval, t.monthDay, t.weekday, t.weekdays || t.repeatWeekdays);
      if (!next || next <= currentDate) break;
      currentDate = next;
      safety++;
    }
    return false;
  };

  // Categorize each task into TODAY/PAST or FUTURE
  const isFutureTask = (t) => !occursTodayOrEarlier(t);

  // Today's completions (for "Løst i dag" stat tile)
  const myCompletionsToday = useMemo(() => {
    return completions.filter(c =>
      c.completedBy === profile?.id &&
      c.completedAt?.startsWith(today)
    );
  }, [completions, profile, today]);

  const hour = new Date().getHours();
  const greeting = hour < 10 ? 'Godmorgen' : hour < 18 ? 'Hej' : 'God aften';

  // ============================================================
  // TODAY/OVERDUE — what needs attention now
  // ============================================================

  const fromPartnerTodayAll = useMemo(() => {
    return myActionable
      .filter(t => t.list === 'sent' && t.assignedTo === profile?.id && occursTodayOrEarlier(t))
      .sort((a, b) => nextOccurrenceDate(a).localeCompare(nextOccurrenceDate(b)));
  }, [myActionable, profile, today]);

  const sharedTodayAll = useMemo(() => {
    return myActionable
      .filter(t => t.list === 'shared' && occursTodayOrEarlier(t))
      .sort((a, b) => nextOccurrenceDate(a).localeCompare(nextOccurrenceDate(b)));
  }, [myActionable, today]);

  const myPrivateTodayAll = useMemo(() => {
    return myActionable
      .filter(t => t.list === 'private' && t.createdBy === profile?.id && occursTodayOrEarlier(t))
      .sort((a, b) => nextOccurrenceDate(a).localeCompare(nextOccurrenceDate(b)));
  }, [myActionable, profile, today]);

  // ============================================================
  // FUTURE — coming up in the days/weeks ahead
  // ============================================================

  const fromPartnerFutureAll = useMemo(() => {
    return myActionable
      .filter(t => t.list === 'sent' && t.assignedTo === profile?.id && isFutureTask(t))
      .sort((a, b) => nextOccurrenceDate(a).localeCompare(nextOccurrenceDate(b)));
  }, [myActionable, profile, today]);

  const sharedFutureAll = useMemo(() => {
    return myActionable
      .filter(t => t.list === 'shared' && isFutureTask(t))
      .sort((a, b) => nextOccurrenceDate(a).localeCompare(nextOccurrenceDate(b)));
  }, [myActionable, today]);

  const myPrivateFutureAll = useMemo(() => {
    return myActionable
      .filter(t => t.list === 'private' && t.createdBy === profile?.id && isFutureTask(t))
      .sort((a, b) => nextOccurrenceDate(a).localeCompare(nextOccurrenceDate(b)));
  }, [myActionable, profile, today]);

  const upcomingCount = fromPartnerFutureAll.length + sharedFutureAll.length + myPrivateFutureAll.length;

  // ============================================================
  // DERIVED — preview slices and state checks
  // ============================================================

  const fromPartnerTodayPreview = fromPartnerTodayAll.slice(0, 3);
  const sharedTodayPreview = sharedTodayAll.slice(0, 3);
  const myPrivateTodayPreview = myPrivateTodayAll.slice(0, 3);

  const fromPartnerFuturePreview = fromPartnerFutureAll.slice(0, 3);
  const sharedFuturePreview = sharedFutureAll.slice(0, 3);
  const myPrivateFuturePreview = myPrivateFutureAll.slice(0, 3);

  const completionsPreview = myCompletionsToday.slice(0, 3);

  // Today is "all done" if there are no actionable items today/overdue
  const todayActionableCount = fromPartnerTodayAll.length + sharedTodayAll.length + myPrivateTodayAll.length;
  const hasFutureItems = upcomingCount > 0;
  const hasCompletionsToday = myCompletionsToday.length > 0;

  // Show fanfare card whenever today has nothing left to do AND
  // there's evidence the user has been active (either completed something today or has future tasks)
  const showAllDone = todayActionableCount === 0 && (hasCompletionsToday || hasFutureItems);

  // Show empty-day card only if truly nothing exists
  const isCompletelyEmpty = todayActionableCount === 0 && !hasFutureItems && !hasCompletionsToday;

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
          {greeting}, {profile?.name?.split(' ')[0]}
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: theme.textMuted }}>
          {formatToday()}
        </p>
      </div>

      {/* Action cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: partner ? 'repeat(3, 1fr)' : '1fr',
        gap: 8,
        marginBottom: 20,
      }}>
        <ActionCard theme={theme} icon={Plus} label="Tilføj opgave til dig selv" onClick={() => onAddTask('mine')} color={theme.textMuted} />
        {partner && <ActionCard theme={theme} icon={Send} label={`Send opgaver til ${partnerFirstName}`} onClick={() => onAddTask('send')} color={theme.coral} />}
        {partner && <ActionCard theme={theme} icon={Users} label="Tilføj fælles opgave" onClick={() => onAddTask('shared')} color={theme.teal} />}
      </div>

      {/* Stat tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 20 }}>
        <StatTile
          theme={theme}
          icon={Inbox}
          value={upcomingCount}
          label="Kommende opgaver"
          onClick={() => onGoToTasks?.('all')}
        />
        <StatTile
          theme={theme}
          icon={Star}
          value={myCompletionsToday.length}
          label="Løst i dag"
          color={theme.gold}
          onClick={onScrollToCompletedToday}
        />
        {pair ? (
          <StatTile
            theme={theme}
            icon={Heart}
            value={pairStats?.loveScore || 0}
            label="Kærlighedspoint"
            color={theme.coral}
            onClick={onGoToStats}
          />
        ) : (
          <StatTile
            theme={theme}
            icon={Flame}
            value={myCompletionsToday.length}
            label="I dag"
            color={theme.coral}
            onClick={onScrollToCompletedToday}
          />
        )}
      </div>

      {/* Empty / All-done states */}
      {isCompletelyEmpty && (
        <EmptyDay theme={theme} onAddTask={() => onAddTask('mine')} />
      )}

      {showAllDone && (
        <AllDoneCard theme={theme} />
      )}

      {/* ============ TODAY / OVERDUE ============ */}

      {fromPartnerTodayPreview.length > 0 && partner && (
        <Section
          theme={theme}
          title={`Opgaver fra ${partnerFirstName}`}
          subtitle={fromPartnerTodayAll.length > 3 ? `Viser 3 af ${fromPartnerTodayAll.length}` : null}
          icon={Send}
          accent={theme.coral}
        >
          {fromPartnerTodayPreview.map(t => (
            <TaskRow key={t.id} theme={theme} task={t} currentUserId={profile?.id} partner={partner}
              onComplete={onComplete} onDelete={onDelete} onEdit={onEdit} />
          ))}
          {fromPartnerTodayAll.length > 3 && (
            <SeeAllButton theme={theme} onClick={() => onGoToTasks?.('received')} label={`Vis alle opgaver fra ${partnerFirstName}`} />
          )}
        </Section>
      )}

      {sharedTodayPreview.length > 0 && (
        <Section
          theme={theme}
          title="Fælles opgaver"
          subtitle={sharedTodayAll.length > 3 ? `Viser 3 af ${sharedTodayAll.length}` : null}
          icon={Users}
          accent={theme.teal}
        >
          {sharedTodayPreview.map(t => (
            <TaskRow key={t.id} theme={theme} task={t} currentUserId={profile?.id} partner={partner}
              onComplete={onComplete} onDelete={onDelete} onEdit={onEdit} />
          ))}
          {sharedTodayAll.length > 3 && (
            <SeeAllButton theme={theme} onClick={() => onGoToTasks?.('shared')} label="Vis alle fælles opgaver" />
          )}
        </Section>
      )}

      {myPrivateTodayPreview.length > 0 && (
        <Section
          theme={theme}
          title="Mine egne opgaver"
          subtitle={myPrivateTodayAll.length > 3 ? `Viser 3 af ${myPrivateTodayAll.length}` : null}
          accent={theme.textMuted}
        >
          {myPrivateTodayPreview.map(t => (
            <TaskRow key={t.id} theme={theme} task={t} currentUserId={profile?.id} partner={partner}
              onComplete={onComplete} onDelete={onDelete} onEdit={onEdit} />
          ))}
          {myPrivateTodayAll.length > 3 && (
            <SeeAllButton theme={theme} onClick={() => onGoToTasks?.('private')} label="Vis alle mine egne opgaver" />
          )}
        </Section>
      )}

      {/* ============ KOMMENDE OPGAVER ============ */}

      {hasFutureItems && (
        <UpcomingHeader theme={theme} count={upcomingCount} />
      )}

      {fromPartnerFuturePreview.length > 0 && partner && (
        <Section
          theme={theme}
          title={`Kommende opgaver fra ${partnerFirstName}`}
          subtitle={fromPartnerFutureAll.length > 3 ? `Viser 3 af ${fromPartnerFutureAll.length}` : null}
          icon={Send}
          accent={theme.coral}
        >
          {fromPartnerFuturePreview.map(t => (
            <TaskRow key={t.id} theme={theme} task={t} currentUserId={profile?.id} partner={partner}
              onComplete={onComplete} onDelete={onDelete} onEdit={onEdit} />
          ))}
          {fromPartnerFutureAll.length > 3 && (
            <SeeAllButton theme={theme} onClick={() => onGoToTasks?.('received')} label={`Se alle kommende opgaver fra ${partnerFirstName}`} />
          )}
        </Section>
      )}

      {sharedFuturePreview.length > 0 && (
        <Section
          theme={theme}
          title="Kommende fælles opgaver"
          subtitle={sharedFutureAll.length > 3 ? `Viser 3 af ${sharedFutureAll.length}` : null}
          icon={Users}
          accent={theme.teal}
        >
          {sharedFuturePreview.map(t => (
            <TaskRow key={t.id} theme={theme} task={t} currentUserId={profile?.id} partner={partner}
              onComplete={onComplete} onDelete={onDelete} onEdit={onEdit} />
          ))}
          {sharedFutureAll.length > 3 && (
            <SeeAllButton theme={theme} onClick={() => onGoToTasks?.('shared')} label="Se alle kommende fællesopgaver" />
          )}
        </Section>
      )}

      {myPrivateFuturePreview.length > 0 && (
        <Section
          theme={theme}
          title="Mine kommende egne opgaver"
          subtitle={myPrivateFutureAll.length > 3 ? `Viser 3 af ${myPrivateFutureAll.length}` : null}
          accent={theme.textMuted}
        >
          {myPrivateFuturePreview.map(t => (
            <TaskRow key={t.id} theme={theme} task={t} currentUserId={profile?.id} partner={partner}
              onComplete={onComplete} onDelete={onDelete} onEdit={onEdit} />
          ))}
          {myPrivateFutureAll.length > 3 && (
            <SeeAllButton theme={theme} onClick={() => onGoToTasks?.('private')} label="Se alle dine egne kommende opgaver" />
          )}
        </Section>
      )}

      {/* ============ LØST I DAG ============ */}

      {myCompletionsToday.length > 0 && (
        <Section theme={theme} title="Løst i dag" icon={Star} accent={theme.gold}
          subtitle={myCompletionsToday.length > 3 ? `Viser 3 af ${myCompletionsToday.length}` : null}
          id="completed-today-section"
        >
          {completionsPreview.map(c => (
            <div key={c.id} style={{
              padding: '10px 12px',
              background: theme.bgCard,
              border: `1px solid ${theme.borderSubtle}`,
              borderRadius: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              opacity: 0.7,
            }}>
              <div style={{ flex: 1, fontSize: 14, textDecoration: 'line-through', color: theme.textMuted }}>{c.taskTitle}</div>
              {c.pointsEarned > 0 && (
                <div style={{ fontSize: 11, color: theme.gold, fontWeight: 600 }}>+{c.pointsEarned}</div>
              )}
            </div>
          ))}
          {myCompletionsToday.length > 3 && (
            <SeeAllButton theme={theme} onClick={onGoToStats} label="Se alle løste opgaver i dag" />
          )}
        </Section>
      )}
    </div>
  );
}

// ============================================================
// Sub-components
// ============================================================

function ActionCard({ theme, icon: Icon, label, onClick, color }) {
  return (
    <button onClick={onClick} style={{
      background: theme.bgCard,
      border: `1px solid ${theme.borderSubtle}`,
      borderRadius: 12,
      padding: '14px 8px',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 6,
      color: theme.text,
    }}>
      <Icon size={18} color={color} />
      <span style={{ fontSize: 11, fontWeight: 600, textAlign: 'center', lineHeight: 1.3 }}>{label}</span>
    </button>
  );
}

function StatTile({ theme, icon: Icon, value, label, color, onClick }) {
  const isClickable = !!onClick;
  return (
    <button
      onClick={onClick}
      disabled={!isClickable}
      style={{
        background: theme.bgCard,
        border: `1px solid ${theme.borderSubtle}`,
        borderRadius: 12,
        padding: '12px 10px',
        cursor: isClickable ? 'pointer' : 'default',
        textAlign: 'left',
        color: theme.text,
        transition: 'transform 0.1s ease',
      }}
      onMouseDown={isClickable ? (e) => { e.currentTarget.style.transform = 'scale(0.98)'; } : undefined}
      onMouseUp={isClickable ? (e) => { e.currentTarget.style.transform = 'scale(1)'; } : undefined}
      onMouseLeave={isClickable ? (e) => { e.currentTarget.style.transform = 'scale(1)'; } : undefined}
    >
      <Icon size={14} color={color || theme.textMuted} style={{ marginBottom: 6 }} />
      <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1, color: color || theme.text }}>{value}</div>
      <div style={{ fontSize: 10, color: theme.textMuted, marginTop: 2 }}>{label}</div>
    </button>
  );
}

function Section({ theme, title, subtitle, icon: Icon, accent, children, id }) {
  return (
    <div style={{ marginBottom: 18 }} id={id}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 8,
        marginBottom: 8,
      }}>
        {Icon && <Icon size={13} color={accent} />}
        <h3 style={{
          margin: 0, fontSize: 11, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: 1,
          color: accent || theme.textMuted,
        }}>{title}</h3>
        {subtitle && (
          <span style={{ fontSize: 10, color: theme.textDim }}>· {subtitle}</span>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {children}
      </div>
    </div>
  );
}

function SeeAllButton({ theme, onClick, label }) {
  return (
    <button onClick={onClick} style={{
      background: 'transparent',
      border: `1px dashed ${theme.border}`,
      color: theme.textMuted,
      padding: '10px 12px',
      borderRadius: 10,
      fontSize: 12,
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      marginTop: 2,
    }}>
      {label} <ChevronRight size={13} />
    </button>
  );
}

function EmptyDay({ theme, onAddTask }) {
  return (
    <div style={{
      textAlign: 'center', padding: '40px 20px',
      background: theme.bgCard,
      border: `1px solid ${theme.borderSubtle}`,
      borderRadius: 14,
      marginBottom: 18,
    }}>
      <div style={{ fontSize: 38, marginBottom: 12 }}>🛋️</div>
      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Slap af — der er ingen opgaver.</h3>
      <p style={{ margin: '6px 0 16px', fontSize: 13, color: theme.textMuted }}>Eller opret en, hvis du har lyst.</p>
      <button onClick={onAddTask} style={{
        background: theme.coral, color: 'white',
        border: 'none', padding: '10px 16px',
        borderRadius: 10, fontSize: 13, fontWeight: 600,
        cursor: 'pointer',
      }}>Opret en opgave</button>
    </div>
  );
}

function AllDoneCard({ theme }) {
  return (
    <div style={{
      textAlign: 'center', padding: '32px 20px',
      background: `linear-gradient(135deg, ${theme.coral}15, ${theme.gold}15)`,
      border: `1px solid ${theme.coral}40`,
      borderRadius: 14, marginBottom: 18,
    }}>
      <div style={{ fontSize: 44, marginBottom: 6 }}>🎉</div>
      <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Hurra, alle opgaverne for i dag er løst</h3>
      <p style={{ margin: '6px 0 0', fontSize: 13, color: theme.textMuted, lineHeight: 1.5 }}>
        Du kan igen slappe af på sofaen
      </p>
    </div>
  );
}

function UpcomingHeader({ theme, count }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '14px 14px',
      background: theme.bgCard,
      border: `1px solid ${theme.borderSubtle}`,
      borderRadius: 12,
      marginBottom: 14,
      marginTop: 6,
    }}>
      <div style={{
        width: 32, height: 32,
        borderRadius: 9,
        background: `${theme.gold}20`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <CalendarClock size={16} color={theme.gold} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>Kommende opgaver</div>
        <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
          {count} {count === 1 ? 'opgave' : 'opgaver'} på vej i de kommende dage
        </div>
      </div>
    </div>
  );
}

function formatToday() {
  const days = ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'];
  const months = ['januar', 'februar', 'marts', 'april', 'maj', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'december'];
  const d = new Date();
  return `${days[d.getDay()]} ${d.getDate()}. ${months[d.getMonth()]}`;
}
